import React, { useEffect, useState } from 'react';
import { AcademicCapIcon, MegaphoneIcon } from '@heroicons/react/24/outline';
import { collection, getDocs, query, where } from 'firebase/firestore';
import db from '../firebase-config.ts';
import { useUser } from './UserProvider.tsx';
import { useNavigate } from 'react-router-dom'; 

// Interfaces
interface Announcement {
  id: string;
  description: string;
  name: string;
  date: string;
  photo: string;
  link: string;
}

interface Event {
  id: string;
  name: string;
  description: string;
  date: string;
}

interface UserData {
  no_matrik: string;
  name: string;
  email: string;
  [key: string]: any;
}

const StudentPortal: React.FC = () => {
  const { user, isAuthenticated } = useUser();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [userData, setUserData] = useState<UserData | null>(null);
  const navigate = useNavigate(); // Initialize navigation
  
  
    useEffect(() => {
        if (!isAuthenticated) {
          navigate('/login'); // Redirect to login page
        }
      }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user || !user.sessionId) {
        console.error('No user session found.');
        return;
      }

      try {
        const usersCollection = collection(db, 'students');
        const q = query(usersCollection, where('uid', '==', user.sessionId));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const [doc] = snapshot.docs;
          const data = doc.data() as UserData;
          setUserData(data);
        } else {
          console.error('No user found for this sessionId.');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    if (isAuthenticated) {
      fetchUserData();
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      const announcementsCollection = collection(db, 'announcements');
      const snapshot = await getDocs(announcementsCollection);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Announcement[];
      setAnnouncements(data);
    };

    const fetchEvents = async () => {
      const eventsCollection = collection(db, 'events');
      const snapshot = await getDocs(eventsCollection);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Event[];
      setEvents(data);
    };

    fetchAnnouncements();
    fetchEvents();
  }, []);

  

  return isAuthenticated ? (
    <div className="min-h-screen text-gray-200 bg-gray-900">
      {/* Hero Section */}
      <header className="py-10 px-8">
        <h1 className="text-4xl uppercase font-bold mb-4">Welcome to Portal IKAS</h1>
        <p className="text-xl">
          Hello, <span className="font-semibold">{userData?.name || 'Student'}</span>!
        </p>
      </header>

      {/* Main Content */}
      <div className="p-6 space-y-16">
        {/* Announcements Section */}
        <section>
          <h2 className="text-3xl font-bold text-blue-400 mb-6 flex items-center gap-2">
            <MegaphoneIcon className="h-8 w-8 text-blue-400" /> Announcements
          </h2>
          {announcements.length === 0 ? (
            <p className="text-gray-400">No announcements available at the moment.</p>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {announcements.map(announcement => (
                <div
                  key={announcement.id}
                  className="bg-gray-800 p-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <img
                    src={announcement.photo}
                    alt={announcement.name}
                    className="w-full h-40 object-cover rounded-t-lg mb-4"
                  />
                  <h3 className="text-2xl font-bold mb-2">{announcement.name}</h3>
                  <p className="text-gray-300 mt-2 text-sm">{announcement.description}</p>
                  <span className="text-gray-500 text-xs block mt-4">{announcement.date}</span>
                  {announcement.link && (
                    <a
                      href={announcement.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-500 mt-2 block text-sm"
                    >
                      Read more
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Divider */}
        <div className="h-1 bg-gradient-to-r from-blue-400 to-green-400 rounded-full" />

        {/* Upcoming Events Section */}
        <section>
          <h2 className="text-3xl font-bold text-green-400 mb-6 flex items-center gap-2">
            <AcademicCapIcon className="h-8 w-8 text-green-400" /> Upcoming Events
          </h2>
          {events.length === 0 ? (
            <p className="text-gray-400">No upcoming events available.</p>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {events.map(event => (
                <div
                  key={event.id}
                  className="bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <h3 className="text-2xl font-bold mb-2">{event.name}</h3>
                  <p className="text-gray-300 mt-2 text-sm">{event.description}</p>
                  <span className="text-gray-500 text-xs block mt-4">{event.date}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  ) : null; 
};

export default StudentPortal;
